// ✅ MIGRATED TO V2 DESIGN SYSTEM - Volvo/Teal Theme
import { Edit, FileText, Plus, Save, Trash2, X, Scale } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { notify } from '@/stores/notificationStore';
import { confirm } from '@/stores/confirmStore';
import { legalMentionsAPI, LegalMention, CreateLegalMentionData, UpdateLegalMentionData } from '@/utils/legalMentionsAPI';
import { logger } from '@/utils/logger';
import { Button, Spinner, Badge } from '@/components/ui-v2';

import { useTranslation } from 'react-i18next';
// Form data type for creating/editing mentions
interface LegalMentionFormData {
  code: string;
  name: string;
  content: string;
  category?: string;
  isActive: boolean;
  isDefault?: boolean;
}

export const LegalMentionsManagement: React.FC = () => {
  const { t } = useTranslation();

  const [mentions, setMentions] = useState<LegalMention[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingMention, setEditingMention] = useState<Partial<LegalMentionFormData>>({});
  const [saving, setSaving] = useState(false);

  // Card classes V2
  const cardClasses = `bg-white/20 backdrop-blur-xl rounded-3xl shadow-xl shadow-gray-300/10
    border border-white/30 p-6 hover:bg-white/70 hover:shadow-2xl hover:-translate-y-1
    transition-all duration-500`;

  const inputClasses = `w-full px-4 py-3 bg-white/40 border border-white/50 rounded-2xl
    focus:ring-2 focus:ring-teal-500 focus:border-teal-500 focus:bg-white
    hover:bg-white hover:border-teal-400 hover:shadow-lg hover:shadow-teal-100/50
    transition-all duration-300 outline-none text-sm`;

  useEffect(() => {
    loadMentions();
  }, []);

  const loadMentions = async () => {
    try {
      setLoading(true);
      setError(null);

      const data = await legalMentionsAPI.getAll();
      setMentions(data.data || []);
    } catch (err) {
      logger.error('Erreur LegalMentionsManagement:', err);
      setError(err instanceof Error ? err.message : 'Erreur inconnue');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (mention: LegalMention) => {
    setEditingId(mention.id);
    setEditingMention({
      code: mention.code,
      name: mention.name,
      content: mention.content,
      category: mention.category,
      isActive: mention.isActive,
      isDefault: mention.isDefault,
    });
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditingMention({});
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      if (editingId) {
        // Update existing mention
        const updateData: UpdateLegalMentionData = {
          name: editingMention.name,
          content: editingMention.content,
          category: editingMention.category,
          isActive: editingMention.isActive,
          isDefault: editingMention.isDefault,
        };
        await legalMentionsAPI.update(editingId, updateData);
      } else {
        // Create new mention - ensure required fields are present
        if (!editingMention.code || !editingMention.name || !editingMention.content) {
          notify.error(t('admin.legalMentions.fillRequired'), t('admin.legalMentions.missingFields'));
          setSaving(false);
          return;
        }
        const createData: CreateLegalMentionData = {
          code: editingMention.code,
          name: editingMention.name,
          content: editingMention.content,
          category: editingMention.category,
          isActive: editingMention.isActive,
          isDefault: editingMention.isDefault,
        };
        await legalMentionsAPI.create(createData);
      }

      await loadMentions();
      handleCancel();
      notify.success(t('admin.legalMentions.savedSuccess'), t('admin.legalMentions.savedSuccessTitle'));
    } catch (err) {
      logger.error('Erreur sauvegarde mention:', err);
      notify.error(
        t('admin.legalMentions.saveErrorMessage', { message: err instanceof Error ? err.message : t('common.errors.unknown') }),
        t('admin.legalMentions.saveErrorTitle')
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    const confirmed = await confirm({
      title: t('admin.legalMentions.deleteTitle'),
      message: t('admin.legalMentions.deleteConfirmMessage'),
      confirmText: t('common.actions.delete'),
      cancelText: t('common.actions.cancel'),
      variant: 'danger',
    });
    if (!confirmed) return;

    try {
      await legalMentionsAPI.delete(id);
      notify.success(t('admin.legalMentions.deletedSuccess'), t('admin.legalMentions.deletedSuccessTitle'));
      await loadMentions();
    } catch (err) {
      logger.error('Erreur suppression mention:', err);
      notify.error(
        t('admin.legalMentions.deleteErrorMessage', { message: err instanceof Error ? err.message : t('common.errors.unknown') }),
        t('admin.legalMentions.deleteErrorTitle')
      );
    }
  };

  const handleNew = () => {
    setEditingId(null);
    setEditingMention({
      code: '',
      name: '',
      content: '',
      category: '',
      isActive: true,
      isDefault: false,
    });
  };

  if (loading) {
    return (
      <div className="px-8 py-12 flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="px-8 py-12">
        <div className={cardClasses}>
          <div className="text-center py-8">
            <div className="p-4 bg-red-100 rounded-2xl w-fit mx-auto mb-4">
              <FileText className="w-12 h-12 text-red-500" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('admin.legalMentions.loadError')}</h3>
            <p className="text-gray-600 mb-6">{error}</p>
            <Button variant="secondary" onClick={loadMentions}>
              {t('admin.legalMentions.retry')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="px-8 pt-8 pb-12 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-teal-100 rounded-2xl">
            <Scale className="w-8 h-8 text-teal-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t('admin.legalMentions.title')}</h1>
            <p className="text-gray-500 mt-0.5">
              {t('admin.legalMentions.subtitle')}
            </p>
          </div>
        </div>

        <Button
          variant="secondary"
          onClick={handleNew}
          icon={<Plus className="w-4 h-4" />}
        >
          {t('admin.legalMentions.newMention')}
        </Button>
      </div>

      {/* Info Box */}
      <div className="bg-teal-50/30 backdrop-blur-sm border border-teal-200/50 rounded-2xl p-5 hover:bg-teal-50/50 transition-all duration-300">
        <div className="flex items-start gap-4">
          <div className="p-2 bg-teal-100 rounded-xl">
            <FileText className="w-5 h-5 text-teal-600" />
          </div>
          <div>
            <h4 className="font-semibold text-teal-900">{t('admin.legalMentions.infoTitle')}</h4>
            <p className="text-sm text-teal-800 mt-1">
              {t('admin.legalMentions.infoDescFull')}
            </p>
          </div>
        </div>
      </div>

      {/* Formulaire d'édition */}
      {(editingId !== null || Object.keys(editingMention).length > 0) && (
        <div className={cardClasses}>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            {editingId ? t('admin.legalMentions.editTitle') : t('admin.legalMentions.newTitle')}
          </h3>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('admin.legalMentions.codeLabel')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={editingMention.code || ''}
                  onChange={(e) =>
                    setEditingMention((prev) => ({ ...prev, code: e.target.value }))
                  }
                  className={inputClasses}
                  placeholder={t('admin.legalMentions.placeholders.code')}
                  disabled={!!editingId}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('admin.legalMentions.nameLabel')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={editingMention.name || ''}
                  onChange={(e) =>
                    setEditingMention((prev) => ({ ...prev, name: e.target.value }))
                  }
                  className={inputClasses}
                  placeholder={t('admin.legalMentions.placeholders.name')}
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('admin.legalMentions.categoryLabel')}
              </label>
              <input
                type="text"
                value={editingMention.category || ''}
                onChange={(e) =>
                  setEditingMention((prev) => ({ ...prev, category: e.target.value }))
                }
                className={inputClasses}
                placeholder={t('admin.legalMentions.placeholders.category')}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('admin.legalMentions.contentLabel2')} <span className="text-red-500">*</span>
              </label>
              <textarea
                value={editingMention.content || ''}
                onChange={(e) =>
                  setEditingMention((prev) => ({ ...prev, content: e.target.value }))
                }
                rows={4}
                className={`${inputClasses} font-mono`}
                placeholder={t('admin.legalMentions.placeholders.content')}
              />
            </div>
            <div className="flex items-center">
              <input
                type="checkbox"
                id="isActive"
                checked={editingMention.isActive || false}
                onChange={(e) =>
                  setEditingMention((prev) => ({ ...prev, isActive: e.target.checked }))
                }
                className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded"
              />
              <label htmlFor="isActive" className="ml-2 block text-sm text-gray-900">
                {t('admin.legalMentions.activeCheckbox')}
              </label>
            </div>
            <div className="flex justify-end gap-3 pt-4 border-t border-white/30">
              <Button variant="ghost" onClick={handleCancel} icon={<X className="w-4 h-4" />}>
                {t('common.actions.cancel')}
              </Button>
              <Button
                variant="secondary"
                onClick={handleSave}
                disabled={saving}
                icon={saving ? undefined : <Save className="w-4 h-4" />}
              >
                {saving ? t('admin.legalMentions.saving') : t('admin.legalMentions.save')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* {t('admin.legalMentions.listTitle')} */}
      {mentions.length === 0 ? (
        <div className={cardClasses}>
          <div className="text-center py-8">
            <div className="p-4 bg-gray-100 rounded-2xl w-fit mx-auto mb-4">
              <FileText className="w-12 h-12 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('admin.legalMentions.noMentions')}</h3>
            <p className="text-gray-600 mb-6">{t('admin.legalMentions.noMentionsDesc')}</p>
            <Button
              variant="secondary"
              onClick={handleNew}
              icon={<Plus className="w-4 h-4" />}
            >
              {t('admin.legalMentions.createMention')}
            </Button>
          </div>
        </div>
      ) : (
        <div className={`${cardClasses} p-0 overflow-hidden`}>
          <div className="px-6 py-4 border-b border-white/30">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <FileText className="w-5 h-5 text-teal-600" />
              {t('admin.legalMentions.listHeading')}
            </h3>
          </div>
          <ul className="divide-y divide-white/20">
            {mentions.map((mention) => (
              <li key={mention.id} className="px-6 py-4 hover:bg-white/30 transition-colors">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <h3 className="text-sm font-semibold text-gray-900">{mention.name}</h3>
                      <Badge>{mention.code}</Badge>
                      {mention.category && <Badge variant="info">{mention.category}</Badge>}
                      {mention.isActive ? (
                        <Badge variant="success">{t('admin.legalMentions.isActive')}</Badge>
                      ) : (
                        <Badge variant="alert">{t('admin.legalMentions.isInactive')}</Badge>
                      )}
                      {mention.isDefault && <Badge variant="warning">{t('admin.legalMentions.isDefault')}</Badge>}
                    </div>
                    <p className="text-sm text-gray-600 line-clamp-2">{mention.content}</p>
                  </div>
                  <div className="flex gap-1">
                    <button
                      onClick={() => handleEdit(mention)}
                      className="p-2 text-teal-600 hover:bg-teal-50 rounded-xl transition-colors"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(mention.id)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-xl transition-colors"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};
